import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { initials } from "@/lib/format";
import { FileSearch, Activity, ClipboardCheck, Boxes, ChevronRight } from "lucide-react";

const CONFIG = {
  assessment: { title: "Assessments", subtitle: "Clinical assessments across active cases", crumb: "Assessments", icon: FileSearch,
    render: (c) => c.findings?.join(" · ") || "Assessment pending" },
  diagnosis: { title: "Diagnoses", subtitle: "Clinical diagnoses across active cases", crumb: "Diagnoses", icon: Activity,
    render: (c) => c.diagnosis || "Diagnosis pending" },
  treatment_plan: { title: "Treatment Plans", subtitle: "Phased treatment plans across cases", crumb: "Treatment Plans", icon: ClipboardCheck,
    render: (c) => `${(c.treatment_plan || []).length} phases · ${c.treatment}` },
  implant: { title: "Implant Planning", subtitle: "Implant site planning and measurements", crumb: "Implant Planning", icon: Boxes,
    render: (c) => `Tooth ${c.implant_site?.tooth || "—"} · Bone ${c.implant_site?.bone_height || "—"}` },
};

export default function CaseDerived({ kind }) {
  const cfg = CONFIG[kind];
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/cases").then((r) => {
      let data = r.data;
      if (kind === "diagnosis") data = data.filter((c) => (c.stages_done || []).includes("diagnosis") || c.diagnosis);
      if (kind === "treatment_plan") data = data.filter((c) => (c.treatment_plan || []).length);
      setItems(data);
    }).catch(() => setItems([]));
  }, [kind]);

  return (
    <div>
      <PageHeader title={cfg.title} subtitle={cfg.subtitle} breadcrumb={["Clinical", cfg.crumb]} />
      {items && items.length === 0 ? (
        <EmptyState icon={cfg.icon} title={`No ${cfg.title.toLowerCase()} yet`} description="These will appear as cases progress through the clinical journey." />
      ) : (
        <div className="space-y-3">
          {(items || []).map((c) => (
            <SurfaceCard key={c.id} className="p-4 cursor-pointer hover:border-[#00A85A]/40" onClick={() => navigate(`/cases/${c.id}`)}>
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F7F0] dark:bg-[#00A85A]/15 text-xs font-bold text-[#008F4D] dark:text-[#3FD98A]">{initials(c.patient_name)}</div>
                <div className="flex-1 min-w-0"><div className="text-sm font-bold text-[#101516] dark:text-white truncate">{c.patient_name} · {c.case_id}</div><div className="text-xs text-[#8B9698] truncate">{cfg.render(c)}</div></div>
                <StatusBadge status="in_progress" label={c.stage.replace(/_/g, " ")} />
                <ChevronRight className="h-4 w-4 text-[#8B9698]" />
              </div>
            </SurfaceCard>
          ))}
        </div>
      )}
    </div>
  );
}
